import { useCallback, useEffect, useMemo, useState } from "react"
import { Table } from "components/ui/tables/Table/Table"
import { PricingTableColumns, type TPricingCellKey } from "./PricingTableColumns"
import { getNextCell, type TSelectedCell } from "./PricingTable.helper"
import type { TPriceField, TPricingTableRow } from "types/modelNumbers.type"
import style from "./PricingTable.module.scss"

const { table, disabledCell, selectedCellTd, editingCellTd, invalidCellTd, cellValue, cellInput } = style

type Props = {
	data: TPricingTableRow[]
	onCellValueChange?: (seriesId: number, field: TPriceField, value: number | undefined) => void
	allRowsEnabled?: boolean
	invalidSeriesIds?: Set<number>
}

const isPriceField = (cellKey: TPricingCellKey): cellKey is TPriceField => cellKey !== "series"

export const PricingTable = ({ data, onCellValueChange, allRowsEnabled = false, invalidSeriesIds }: Props) => {
	const [selectedCellState, setSelectedCellState] = useState<TSelectedCell | null>(null)
	const [editingCellState, setEditingCellState] = useState<TSelectedCell | null>(null)
	const [editingValue, setEditingValue] = useState<number | null>(null)

	useEffect(() => {
		const onKeyDownHandle = (event: KeyboardEvent) => {
			if (!selectedCellState || editingCellState) return

			const activeEl = document.activeElement
			if (
				activeEl instanceof HTMLInputElement ||
				activeEl instanceof HTMLTextAreaElement ||
				(activeEl instanceof HTMLElement && activeEl.isContentEditable)
			)
				return

			if (event.key === "Delete" || event.key === "Backspace") {
				event.preventDefault()
				onCellValueChange?.(selectedCellState.seriesId, selectedCellState.field, undefined)
			}

			if (event.key === "Enter") {
				event.preventDefault()
				const row = data.find((row) => row.series_id === selectedCellState.seriesId)
				const currentValue = row ? row[selectedCellState.field] : undefined
				setEditingCellState(selectedCellState)
				setEditingValue(typeof currentValue === "number" ? currentValue : null)
			}

			if (event.key === "Tab") {
				event.preventDefault()
				const direction = event.shiftKey ? "backward" : "forward"
				const nextCell = getNextCell(selectedCellState.seriesId, selectedCellState.field, data, direction)
				if (nextCell) {
					setSelectedCellState(nextCell)
				}
			}
		}

		window.addEventListener("keydown", onKeyDownHandle)

		return () => {
			window.removeEventListener("keydown", onKeyDownHandle)
		}
	}, [selectedCellState, editingCellState, onCellValueChange, data])

	const commitEditingHandle = useCallback(() => {
		if (!editingCellState) return

		const parsedValue = editingValue === null ? undefined : editingValue

		if (parsedValue !== undefined && Number.isNaN(parsedValue)) {
			setEditingCellState(null)
			setEditingValue(null)
			return
		}

		onCellValueChange?.(editingCellState.seriesId, editingCellState.field, parsedValue)
		setEditingCellState(null)
		setEditingValue(null)
	}, [editingCellState, editingValue, onCellValueChange])

	const columns = useMemo(() => {
		const getCellClassName = ({ cellKey, seriesId }: { cellKey: TPricingCellKey; seriesId: number }) => {
			if (!isPriceField(cellKey)) return ""

			const isEditing = editingCellState?.seriesId === seriesId && editingCellState?.field === cellKey
			const isSelected = !isEditing && selectedCellState?.seriesId === seriesId && selectedCellState?.field === cellKey

			if (isEditing) return editingCellTd
			if (isSelected) return selectedCellTd

			if (invalidSeriesIds?.has(seriesId)) {
				const row = data.find((r) => r.series_id === seriesId)
				if (row && typeof row[cellKey] !== "number") {
					return invalidCellTd
				}
			}

			return ""
		}

		const renderCellValue = ({
			value,
			hasPrices,
			cellKey,
			seriesId,
		}: {
			value: string | number | undefined
			hasPrices: boolean
			cellKey: TPricingCellKey
			seriesId: number
		}) => {
			const isPriceCell = isPriceField(cellKey)
			const isEditing = isPriceCell && editingCellState?.seriesId === seriesId && editingCellState?.field === cellKey
			const formattedValue = typeof value === "number" ? `$${value}` : value ?? ""
			const isDisabled = !allRowsEnabled && !hasPrices
			const className = [cellValue, isDisabled ? disabledCell : ""].join(" ").trim()

			if (isEditing) {
				return (
					<input
						type="number"
						inputMode="decimal"
						className={cellInput}
						value={editingValue ?? ""}
						name="price"
						autoFocus
						onChange={(event) => {
							const nextValue = event.target.value
							setEditingValue(nextValue === "" ? null : Number(nextValue))
						}}
						onBlur={commitEditingHandle}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault()
								commitEditingHandle()
							}

							if (event.key === "Escape") {
								event.preventDefault()
								setEditingCellState(null)
								setEditingValue(null)
							}

							if (event.key === "Tab" && editingCellState) {
								event.preventDefault()
								commitEditingHandle()
								const direction = event.shiftKey ? "backward" : "forward"
								const nextCell = getNextCell(editingCellState.seriesId, editingCellState.field, data, direction)
								if (nextCell) {
									const nextRow = data.find((row) => row.series_id === nextCell.seriesId)
									const nextValue = nextRow ? nextRow[nextCell.field] : null
									setSelectedCellState(nextCell)
									setEditingCellState(nextCell)
									setEditingValue(typeof nextValue === "number" ? nextValue : null)
								}
							}
						}}
					/>
				)
			}

			return (
				<span
					className={className}
					onClick={() => {
						if (!isPriceCell) return
						setSelectedCellState({ seriesId, field: cellKey })
					}}
					onDoubleClick={() => {
						if (!isPriceCell) return
						setSelectedCellState({ seriesId, field: cellKey })
						setEditingCellState({ seriesId, field: cellKey })
						setEditingValue(typeof value === "number" ? value : null)
					}}
				>
					{formattedValue}
				</span>
			)
		}

		return PricingTableColumns({ renderCellValue, getCellClassName })
	}, [editingCellState, editingValue, selectedCellState, commitEditingHandle, data, allRowsEnabled, invalidSeriesIds])

	return <Table className={table} columns={columns} data={data} pagination={false} bordered />
}
